import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoConfig, MARKETING_ORIGIN } from './seo';

const setMeta = (
  selector: string,
  attribute: 'name' | 'property',
  key: string,
  content: string,
) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    element.dataset.poplearnSeo = 'true';
    document.head.appendChild(element);
  }

  element.content = content;
};

export default function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    const seo = getSeoConfig(location.pathname);

    document.title = seo.title;
    setMeta('meta[name="description"]', 'name', 'description', seo.description);
    setMeta('meta[name="robots"]', 'name', 'robots', seo.robots);
    setMeta('meta[property="og:title"]', 'property', 'og:title', seo.title);
    setMeta(
      'meta[property="og:description"]',
      'property',
      'og:description',
      seo.description,
    );
    setMeta('meta[property="og:type"]', 'property', 'og:type', seo.type);
    setMeta('meta[property="og:image"]', 'property', 'og:image', seo.image);
    setMeta(
      'meta[property="og:site_name"]',
      'property',
      'og:site_name',
      'Poplearn',
    );
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
    setMeta(
      'meta[name="twitter:description"]',
      'name',
      'twitter:description',
      seo.description,
    );
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', seo.image);

    const existingCanonical =
      document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (seo.canonical) {
      const canonical = existingCanonical || document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = seo.canonical;
      canonical.dataset.poplearnSeo = 'true';
      if (!existingCanonical) {
        document.head.appendChild(canonical);
      }
      setMeta('meta[property="og:url"]', 'property', 'og:url', seo.canonical);
    } else {
      existingCanonical?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }

    document.head
      .querySelectorAll('script[data-poplearn-seo-schema]')
      .forEach((script) => script.remove());

    seo.structuredData.forEach((value) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.poplearnSeoSchema = 'true';
      script.text = JSON.stringify(value);
      document.head.appendChild(script);
    });

    document.documentElement.lang = 'en';
    document.documentElement.dataset.siteOrigin = MARKETING_ORIGIN;
  }, [location.pathname]);

  return null;
}
